/**
 * IS THIS A MANAGEMENT LINK WE MAY PUT BEHIND A BUTTON?
 *
 * The draft contract's `CreateBookingResult.managementUrl` is a BEARER
 * CREDENTIAL: whoever holds it can view and, where policy allows, cancel one
 * appointment. The success screen offers "Manage" only for a URL that is
 * unambiguously this environment's own appointment page:
 *
 *   - https, with no credentials, query or fragment;
 *   - host exactly this surface's own host (`config.webHost`), so a dev build
 *     never links to prod and a crafted host can never be linked at all;
 *   - path exactly `/a/<one segment>`, the route that serves `/a/**` with
 *     `Referrer-Policy: no-referrer`.
 *
 * Anything else — including a URL that is merely unusual — is treated as ABSENT.
 * That is the safe direction: an absent link shows the authoritative booking
 * details and a help route, while a wrongly accepted one would hand a person a
 * credential for somewhere we cannot vouch for.
 *
 * The URL itself is never logged or reported. Callers get it back only to put
 * it in one `href`.
 */
const TOKEN_SEGMENT = /^[A-Za-z0-9_-]{16,512}$/;

export function acceptManagementUrl(raw: unknown, webHost: string): string | null {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > 2048) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  if (url.username || url.password) return null;
  if (url.search || url.hash || raw.includes("?") || raw.includes("#")) return null;
  if (url.port) return null;
  if (url.hostname !== webHost) return null;
  const segments = url.pathname.split("/");
  // ["", "a", "<token>"] exactly.
  if (segments.length !== 3 || segments[0] !== "" || segments[1] !== "a") return null;
  const token = segments[2] ?? "";
  if (!TOKEN_SEGMENT.test(token)) return null;
  return url.href;
}
